import apiClient from '../services/api/client';

// ============================================
// TYPES
// ============================================

export interface Product {
  _id?: string;
  id?: string;
  sku: string;
  title: string;
  description: string;
  category: string;
  brand: string;
  basePrice: number;
  stock: number;
  image: string;
  images: string[];
  weight?: number;
  height?: number;
  width?: number;
  length?: number;
  mappingStatus: 'complete' | 'partial' | 'pending';
  channels: Array<{
    marketplaceId: string;
    marketplaceName: string;
    status: 'synced' | 'error' | 'pending';
    price?: number;
    stock?: number;
    lastSync?: string;
    externalId?: string;
    url?: string;          // link direto do anúncio no marketplace
    errors?: Array<{
      code: string;
      message: string;
      field?: string;
      severity: string;
      timestamp: string;
    }>;
  }>;
  attributes?: Array<{
    name: string;
    value: string;
    required: boolean;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductFilters {
  search?: string;
  category?: string;
  marketplace?: string;
  status?: 'synced' | 'error' | 'pending';
  page?: number;
  limit?: number;
  sort?: 'recent' | 'sku' | 'title';
}

export interface ProductsResponse {
  products: Product[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CreateProductData {
  sku: string;
  title: string;
  description: string;
  category: string;
  brand: string;
  basePrice: number;
  stock: number;
  images: string[];
  weight?: number;
  height?: number;
  width?: number;
  length?: number;
  marketplaces: string[];
  attributes?: Array<{
    name: string;
    value: string;
    required: boolean;
  }>;
  // Campos específicos por marketplace
  [key: string]: any;
}

// ============================================
// SERVICE
// ============================================

export const ProductService = {
  /**
   * Lista produtos com filtros e paginação
   * GET /site/api/hub/products
   */
  async getProducts(filters?: ProductFilters): Promise<ProductsResponse> {
    try {
      const response = await apiClient.get<ProductsResponse>('/products', {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      console.error('Erro ao buscar produtos:', error);
      throw new Error(error.message || 'Erro ao buscar produtos');
    }
  },

  /**
   * Busca detalhes de um produto específico
   * GET /site/api/hub/products/:id
   */
  async getProduct(productId: string): Promise<Product> {
    try {
      const response = await apiClient.get<Product>(`/products/${productId}`);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao buscar detalhes do produto:', error);
      throw new Error(error.message || 'Erro ao buscar detalhes do produto');
    }
  },

  /**
   * Cria um novo produto
   * POST /site/api/hub/products
   */
  async createProduct(productData: CreateProductData): Promise<{ message: string; productId: string }> {
    try {
      const response = await apiClient.post('/products', productData);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao criar produto:', error);
      throw new Error(error?.response?.data?.error || error.message || 'Erro ao criar produto');
    }
  },

  /**
   * Atualiza um produto existente
   * PUT /site/api/hub/products/:id
   */
  async updateProduct(
    productId: string,
    updates: Partial<CreateProductData>
  ): Promise<{ message: string }> {
    try {
      const response = await apiClient.put(`/products/${productId}`, updates);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao atualizar produto:', error);
      throw new Error(error.message || 'Erro ao atualizar produto');
    }
  },

  /**
   * Sincroniza produto com marketplaces específicos
   * POST /site/api/hub/products/:id/sync
   */
  async syncProduct(
    productId: string,
    marketplaces: string[]
  ): Promise<{
    message: string;
    results: Array<{
      marketplaceId: string;
      success: boolean;
      message: string;
    }>;
  }> {
    try {
      const response = await apiClient.post(`/products/${productId}/sync`, {
        marketplaces,
      });
      return response.data;
    } catch (error: any) {
      console.error('Erro ao sincronizar produto:', error);
      throw new Error(error.message || 'Erro ao sincronizar produto');
    }
  },

  /**
   * Deleta um produto
   * DELETE /site/api/hub/products/:id
   */
  async deleteProduct(productId: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete(`/products/${productId}`);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao deletar produto:', error);
      throw new Error(error.message || 'Erro ao deletar produto');
    }
  },
};

export default ProductService;
